import { logAction } from '../../common/actionLogger.ts';
import {
    byIdInDomain,
    byIdInDomainThrows,
    byIdThrows,
    isClusterPeer,
    isLocalPeer,
    setPeer,
    updatePeer,
    visiblePeers
} from '../../state/peers.ts';
import { DomainKeys, type Peer, type State } from '../../state/types/state.ts';
import type { AgmPeer } from '../../state/types/agm.ts';
import { methodsAdded, methodsRemoved } from './messages.ts';
import type { Id, MethodDef, MethodDefWithParsedRestrictions, PeerMethods } from '../../state/types/mthds.ts';
import * as rst from '../../restrictions.ts';
import type { HandleReturnType } from '../../domain.ts';
import { broadcast, isRemoteSource, success } from '../../common/messages.ts';
import type { Register, Unregister } from './types/messages.ts';
import { agmDomainUri, agmUnregistrationFailure, reasonMethodRemoved } from './constants.ts';
import { peerToAddress } from '../../address.ts';
import { nodeId } from '../../common/ids.ts';
import { throwReason } from '../../reason.ts';
import { ensureDomain } from '../../common/utilities.ts';
import type { Identity, PeerId, Restrictions, Source } from '../../state/types/common.ts';
import { isClusterMethod } from './state.ts';
import { cancelInterest } from './subscriptions.ts';
import type { MessageType, SendMessageAction } from '../../node.ts';
import { castDraft, type Draft, produce } from 'immer';

function methodToMap(method: MethodDef): MethodDefWithParsedRestrictions {
    const parsedRestrictions: Restrictions | undefined = rst.parse(method.restrictions);
    if (parsedRestrictions) {
        return {...method, parsedRestrictions};
    }
    return method;

}

function mapToMethod(method: MethodDefWithParsedRestrictions): MethodDef {
    const m = {...method};
    delete m['parsedRestrictions'];
    return m;
}

function isVisible(method: MethodDefWithParsedRestrictions, provider:[Source, Identity], consumer: [Source, Identity]): boolean {
    const restrictions = method.parsedRestrictions;
    if (restrictions) {
        return rst.check(restrictions, provider, consumer);
    }
    return true;
}

// Registers methods from a given provider to a given consumer.
//
// It is assumed that the consumer and the provider can see each other
export function registerWithConsumer(state: State, provider: AgmPeer, methods: MethodDefWithParsedRestrictions[], consumer: AgmPeer, options?: MethodOverrides): HandleReturnType {
    const providerId = provider.id;
    const consumerId = consumer.id;
    const existingMethods: PeerMethods = consumer[DomainKeys.Agm]?.methods?.[provider.id] || {};

    const [remote, mthds] = methods
        .filter(isLocalPeer(provider) ? ((m) => true) : remoteMethodsFilter(provider, options?.defaultMethodRestrictions))
        .filter(m => isVisible(m, [provider.source, provider.identity], [consumer.source, consumer.identity]))
        .reduce(([r, ms], v) => {
            r = produce(r, (rd: Draft<PeerMethods>) => {
                rd[v.id] = castDraft(v);
            });
            return [r, ms.concat(mapToMethod(v))];
        }, [existingMethods, new Array<MethodDef>()]);

    if (mthds.length > 0) {
        consumer = produce(consumer, (cd: Draft<AgmPeer>) => {
            cd[DomainKeys.Agm].methods = cd[DomainKeys.Agm].methods || {};
            cd[DomainKeys.Agm].methods[providerId] = castDraft(remote);
        });

        return [
            setPeer(state, consumerId, consumer),
            isLocalPeer(consumer) ? methodsAdded(consumer.source, consumerId, providerId, mthds, provider.source.type) : []] as HandleReturnType
    } else {
        return [state, []];
    }
}

function register(state: State, provider: AgmPeer, methods: MethodDef[], options?: MethodOverrides): HandleReturnType {
    const providerId = provider.id;
    // add the methods to the provider
    const translatedMethods: MethodDefWithParsedRestrictions[] = methods.map((m) => {
        return methodToMap(m);
    });
    provider = translatedMethods.reduce((provider: AgmPeer, method: MethodDefWithParsedRestrictions) => {
        const result: AgmPeer = produce(provider, (p: Draft<AgmPeer>) => {
            p[DomainKeys.Agm].methods = p[DomainKeys.Agm].methods || {};
            p[DomainKeys.Agm].methods[providerId] = p[DomainKeys.Agm].methods[providerId] || {};
            p[DomainKeys.Agm].methods[providerId][method.id] = castDraft(method);
        });
        return result;
    }, provider);
    const updatedState = setPeer(state, providerId, provider);
    logAction(agmDomainUri, () => {
        // todo improve logging of restrictions
        return `[${provider.identity.application}#${provider.id}] registered method(s) [${translatedMethods.map((m) => `${m.name}#${m.id}`).join(', ')}]`;
    });
    // and register them with all eligible consumers
    return visiblePeers(state, DomainKeys.Agm, provider)
        .reduce(([state, actions], consumer: AgmPeer) => {
            const [nextState, nextActions] = registerWithConsumer(state, provider, translatedMethods, consumer, options);
            return [nextState, actions.concat(nextActions)];
        }, [updatedState, []] as HandleReturnType);
}

/*Adds a sequence of methods belonging to a remote peer to all local peers

   Returns a [state (messages)]*/
function remoteRegister(state: State, source: Source, request: Register, options?: MethodOverrides): HandleReturnType {
    const {peer_id: peerId, methods} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Agm);
    if (peer !== undefined && methods !== undefined) {
        return register(state, peer, methods, options);
    }
    return [state, []];
}

export function remoteMethodsFilter(peer: AgmPeer, defaultRestrictions?: MethodOverrides['defaultMethodRestrictions']): (m: MethodDef) => boolean {
    return (m: MethodDef) => {
        if ((m.restrictions === undefined || m.restrictions === null) && defaultRestrictions !== undefined) {
            const parsedRestrictions = defaultRestrictions.call(peer.identity, m.name);
            return isClusterMethod(peer, { parsedRestrictions })
        }
        return true;
    }
}

function localRegister(state: State, source: Source, request: Register, options?: MethodOverrides): HandleReturnType {
    const {request_id: requestId, peer_id: providerId, methods} = request;
    const peer = byIdInDomainThrows(state, providerId, DomainKeys.Agm);
    let [newState, actions] = register(state, peer, methods, options);
    actions = actions.concat([
        methodsAdded(source, providerId, providerId, methods, 'local'),
        success(agmDomainUri, source, requestId, providerId)
    ]);
    if (isClusterPeer(DomainKeys.Agm, peer)) {
        const remoteMethods = methods.filter(remoteMethodsFilter(peer, options?.defaultMethodRestrictions));
        if (remoteMethods.length > 0) {
            const remoteRegister = {...request, methods: remoteMethods};
            actions = actions.concat([broadcast(peerToAddress(nodeId(state.ids), providerId), remoteRegister)]);
        }
    }
    return [newState, actions];
}
export type MethodOverrides = {
    defaultMethodRestrictions?: (this: Identity, name?: string) => Restrictions
}
export function forwardRegister(state: State, source: Source, request: Register, options?: MethodOverrides): HandleReturnType {
    return isRemoteSource(source) ? remoteRegister(state, source, request, options) : localRegister(state, source, request, options);
}

/*Removes all methods of a given provider from a consumer. Generates announcement messages for the
  actually removed methods (those that were visible to the consumer)*/
export function removeMethodsFromConsumer(state: State, providerId: PeerId, consumer: Peer, methodIds?: Id[]): HandleReturnType {
    if (!methodIds) {
        methodIds = Object.keys(consumer[DomainKeys.Agm]?.methods?.[providerId] ?? {});
        if (!methodIds || methodIds.length == 0) {
            return [updatePeer(state, consumer.id, (c: Draft<Peer>) => {
                const domain = c[DomainKeys.Agm]!;
                if (domain.methods) {
                    delete domain.methods[providerId];
                    if (Object.keys(domain.methods).length === 0) {
                        delete domain.methods;
                    }
                }
            }), []];
        }
    }
    // build the list of method ids that are actually present on the consumer

    let removedIds: Set<Id>;
    [consumer, removedIds] = methodIds.reduce(([c, rids], mid) => {
        if (c[DomainKeys.Agm]?.methods?.[providerId]?.[mid]) {
            c = produce(c, (d) => {
                const ms = d[DomainKeys.Agm]?.methods;
                if (ms?.[providerId]?.[mid]) {
                    delete ms[providerId][mid];
                    if (Object.keys(ms[providerId]).length === 0) {
                        delete ms[providerId];
                    }
                    if (Object.keys(ms).length === 0) {
                        delete d[DomainKeys.Agm]?.methods;
                    }
                }
            });
            rids.add(mid);
        }
        return [c, rids];
    }, [consumer, new Set<Id>()]);
    state = setPeer(state, consumer.id, ensureDomain(consumer, DomainKeys.Agm));
    if (removedIds.size == 0) {
        // nothing has been removed - state is unchanged
        return [state, []];
    } else {
        // originally it does not use throws
        const provider = byIdThrows(state, providerId);

        // new state and removal messages
        let messages;
        [state, messages] = cancelInterest(state, consumer, provider, removedIds, reasonMethodRemoved);
        if (isLocalPeer(consumer)) {
            messages = messages.concat(methodsRemoved(consumer.source, consumer.id, providerId, Array.from(removedIds)));
        }
        return [state, messages] as HandleReturnType;
    }
}

function unregister(state: State, peer: AgmPeer, methods: Id[]): HandleReturnType {
    const peerId = peer.id;
    const updatedPeer = ensureDomain(methods.reduce((peer: AgmPeer, mid) => {
        if (peer[DomainKeys.Agm].methods?.[peerId][mid]) {
            peer = produce(peer, (p: Draft<AgmPeer>) => {
                p[DomainKeys.Agm].methods![peerId] = p[DomainKeys.Agm].methods![peerId] || {};
                delete p[DomainKeys.Agm].methods![peerId][mid];
                if (Object.keys(p[DomainKeys.Agm].methods![peerId]).length === 0) {
                    delete p[DomainKeys.Agm].methods![peerId];
                }
                if (Object.keys(p[DomainKeys.Agm].methods!).length === 0) {
                    delete p[DomainKeys.Agm].methods;
                }
            });
        }
        else {
            throwReason(agmUnregistrationFailure, `Unable to unregister missing method ${mid}`);
        }
        return peer;
    }, peer), DomainKeys.Agm);
    let [newState, messages]: HandleReturnType = [setPeer(state, peerId, updatedPeer), new Array<SendMessageAction<MessageType>>()];
    [newState, messages] = visiblePeers(state, DomainKeys.Agm, updatedPeer)
        .filter((p) => {
            return p.id !== peerId;
        })
        .reduce(([s, m]: HandleReturnType, consumer) => {
            const [ns, nm] = removeMethodsFromConsumer(s, peerId, consumer, methods);
            return [ns, m.concat(nm)];
        }, [newState, new Array<SendMessageAction<MessageType>>()]);

    logAction(agmDomainUri, () => {
        return `[${peer.identity.application}#${peerId}] unregistered method(s) [${methods.map(methodId => `${peer[DomainKeys.Agm].methods?.[peerId]?.[methodId]?.name}#${methodId}`).join(', ')}]`;
    });
    return [newState, messages];
}

function remoteUnregister(state: State, request: Unregister): HandleReturnType {
    const {peer_id: peerId, methods} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Agm);
    if (peer) {
        return unregister(state, peer, methods);
    }
    return [state, []];
}

/*Adds a sequence of methods belonging to a local peer to all local peers

   Returns a [state (messages)]*/
function localUnregister(state: State, source: Source, request: Unregister, options?: MethodOverrides): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, methods} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    let [newState, messages] = unregister(state, peer, methods);
    messages = messages.concat([
        methodsRemoved(source, peerId, peerId, methods),
        success(agmDomainUri, source, requestId, peerId),
    ]);
    if (isClusterPeer(DomainKeys.Agm, peer)) {
        const peerMethods = peer[DomainKeys.Agm].methods?.[peerId] ?? {};
        const remoteMethodIds = methods
            .map(mid => peerMethods[mid]) // get the method definitions
            .filter(remoteMethodsFilter(peer, options?.defaultMethodRestrictions))
            .map(m => m.id); // get the method ids
        if (remoteMethodIds.length > 0) {
            const remoteUnregister: Unregister = { ...request, methods: remoteMethodIds };
            messages = messages.concat([broadcast(peerToAddress(nodeId(state.ids), peerId), remoteUnregister)]);
        }
    }
    return [newState, messages];
}

export function forwardUnregister(state: State, source: Source, request: Unregister): HandleReturnType {
    return isRemoteSource(source) ? remoteUnregister(state, request) : localUnregister(state, source, request);
}
